import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { hasAnyRole } from '@court-sync/shared';
import { useAuthStore } from '../../../../store/auth';
import { useGuardianPaths } from '../../../../hooks/useGuardianPaths';
import { useUserScenario } from '../../../../hooks/useUserScenario';
import { useSchedule } from '../../../../hooks/useSchedule';
import { Button, Card, CardContent } from '@/components/ui';
import { DashboardStatCard } from '@/components/dashboard/DashboardStatCard';
import { bookingService } from '../../../../services/booking';
import type { Booking } from '@court-sync/shared';
import toast from 'react-hot-toast';
import { PageHeader } from '@/components/layout/PageHeader';
import { Calendar, Check, Clock } from 'lucide-react';
import { BookingWeeklySchedule } from '../../../../components/schedule/BookingWeeklySchedule';
import { TermLessonCalendar } from '../../../../components/schedule/TermLessonCalendar';
import {
  ScheduleViewHeaderToggle,
  type ScheduleCalendarView,
} from '@/components/schedule/ScheduleViewHeaderToggle';
import { LessonDetailModal } from '../../../../components/bookings/LessonDetailModal';
import { RefundRequestModal } from '../../../../components/bookings/RefundRequestModal';
import { ReportAbsenceFromCalendar } from '../../../../components/schedule/ReportAbsenceFromCalendar';
import { useRefundRequest } from '../../../../hooks/useRefundRequest';
import type { ScheduleOccurrence } from '../../../../hooks/useSchedule';
export default function MyCalendar() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const paths = useGuardianPaths();
  const { children, selfStudent, academyId } = useUserScenario();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [detailOccurrence, setDetailOccurrence] = useState<ScheduleOccurrence | null>(null);
  const [absenceOccurrence, setAbsenceOccurrence] = useState<ScheduleOccurrence | null>(null);
  const [reportSuccess, setReportSuccess] = useState(false);
  const [refundBooking, setRefundBooking] = useState<Booking | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [scheduleView, setScheduleView] = useState<ScheduleCalendarView>('weekly');
  const { requestRefund } = useRefundRequest();

  const {
    occurrences,
    data: scheduleData,
    makeupOccurrenceSet,
    loading: scheduleLoading,
    refetch: refetchSchedule,
  } = useSchedule({
    academyId: academyId ?? undefined,
  });

  const fetchBookings = useCallback(async () => {
    try {
      const response = await bookingService.getMyBookings(academyId ?? undefined);
      setBookings(response.data || []);
    } catch (error) {
      console.error('Error loading bookings:', error);
      toast.error('Failed to load bookings');
    }
  }, [academyId]);

  useEffect(() => {
    if (!user || !hasAnyRole(user, ['guardian', 'student'])) {
      navigate('/dashboard');
      return undefined;
    }
    const timer = setTimeout(() => {
      fetchBookings();
    }, 0);
    return () => clearTimeout(timer);
  }, [user, navigate, fetchBookings]);

  const refundParam = searchParams.get('refund');
  useEffect(() => {
    if (!refundParam || bookings.length === 0) return undefined;
    const booking = bookings.find((b) => b.id === refundParam);
    if (booking && booking.status === 'cancelled') {
      const timer = setTimeout(() => {
        setRefundBooking(booking);
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete('refund');
            return next;
          },
          { replace: true }
        );
      }, 0);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [refundParam, bookings, setSearchParams]);

  const termStart = useMemo(() => {
    const starts: number[] = [];
    if (scheduleData?.terms) {
      Object.values(scheduleData.terms).forEach((t) => starts.push(new Date(t.startDate).getTime()));
    }
    return starts.length > 0 ? new Date(Math.min(...starts)) : undefined;
  }, [scheduleData]);

  const termEnd = useMemo(() => {
    const ends: number[] = [];
    if (scheduleData?.terms) {
      Object.values(scheduleData.terms).forEach((t) => ends.push(new Date(t.endDate).getTime()));
    }
    return ends.length > 0 ? new Date(Math.max(...ends)) : undefined;
  }, [scheduleData]);

  const studentsById = useMemo(() => {
    const map: Record<string, string> = {};
    [...children, ...(selfStudent ? [selfStudent] : [])].forEach((s) => {
      map[s.id] = `${s.firstName} ${s.lastName}`;
    });
    return map;
  }, [children, selfStudent]);

  const handleOccurrenceClick = (occ: ScheduleOccurrence) => {
    setDetailOccurrence(occ);
    setReportSuccess(false);
  };

  const handleReportAbsence = (occ: ScheduleOccurrence) => {
    setAbsenceOccurrence(occ);
    setDetailOccurrence(null);
  };

  const handleAbsenceSuccess = () => {
    const occ = absenceOccurrence;
    setAbsenceOccurrence(null);
    setReportSuccess(true);
    if (occ) setDetailOccurrence(occ);
    refetchSchedule();
    toast.success('Absence reported. You earned a make-up credit!');
  };

  const handleRequestRefund = (occ: ScheduleOccurrence) => {
    const booking = bookings.find((b) => b.id === occ.bookingId);
    if (booking) setRefundBooking(booking);
  };

  const handleRefundConfirm = async (reason?: string) => {
    if (!refundBooking) return;
    await requestRefund(refundBooking.id, reason);
    setRefundBooking(null);
    setDetailOccurrence(null);
    fetchBookings();
  };

  const upcomingBookings = bookings.filter(
    (b) => b.status === 'confirmed' && b.nextLessonDate && new Date(b.nextLessonDate) > new Date()
  );

  if (!user || !hasAnyRole(user, ['guardian', 'student'])) {
    return null;
  }

  return (
    <>
      <div className="contents">
        <PageHeader
          title="My Calendar"
          help={<p>Monthly and weekly views of your lesson times</p>}
          actions={
            <ScheduleViewHeaderToggle
              value={scheduleView}
              onChange={setScheduleView}
              variant="default"
              availableViews={['weekly', 'monthly']}
            />
          }
        />

        <div className="mb-6 grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-4">
          <DashboardStatCard
            icon={<Calendar className="text-primary-600 dark:text-primary-400" aria-hidden />}
            iconBg="bg-primary-100 dark:bg-primary-900/30"
            iconColor="text-primary-600 dark:text-primary-400"
            label="Upcoming"
            value={upcomingBookings.length}
          />
          <DashboardStatCard
            icon={<Check className="text-green-600 dark:text-green-400" aria-hidden />}
            iconBg="bg-green-100 dark:bg-green-900"
            iconColor="text-green-600 dark:text-green-400"
            label="Confirmed"
            value={bookings.filter((b) => b.status === 'confirmed').length}
          />
          <div className="col-span-2 sm:col-span-1">
            <DashboardStatCard
              icon={<Clock className="text-yellow-600 dark:text-yellow-400" aria-hidden />}
              iconBg="bg-yellow-100 dark:bg-yellow-900"
              iconColor="text-yellow-600 dark:text-yellow-400"
              label="Pending"
              value={bookings.filter((b) => b.status === 'pending').length}
            />
          </div>
        </div>

        {scheduleLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : occurrences.length > 0 ? (
          scheduleView === 'monthly' ? (
            <TermLessonCalendar
              occurrences={occurrences}
              termStart={termStart}
              termEnd={termEnd}
              onReportAbsence={handleReportAbsence}
              onRequestRefund={handleRequestRefund}
              lessonsPath={paths?.lessons}
            />
          ) : (
            <BookingWeeklySchedule
              occurrences={occurrences}
              termStart={termStart}
              termEnd={termEnd}
              onOccurrenceClick={handleOccurrenceClick}
              isMakeup={(occ) =>
                makeupOccurrenceSet.has(`${occ.bookingId}:${format(occ.date, 'yyyy-MM-dd')}`)
              }
              studentsById={studentsById}
            />
          )
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="mb-4 text-muted-foreground">No scheduled lessons to display.</p>
              <Button onClick={() => navigate(paths?.lessons ?? '/academy-picker')}>Browse Lessons</Button>
            </CardContent>
          </Card>
        )}
      </div>

      <LessonDetailModal
        occurrence={detailOccurrence}
        isOpen={!!detailOccurrence}
        onClose={() => setDetailOccurrence(null)}
        onReportAbsence={handleReportAbsence}
        onRequestRefund={handleRequestRefund}
        studentName={detailOccurrence ? studentsById[detailOccurrence.studentId] : undefined}
        creditsPath={paths?.credits}
        lessonsPath={paths?.lessons}
        reportSuccess={reportSuccess}
      />

      <RefundRequestModal
        isOpen={!!refundBooking}
        booking={refundBooking}
        lessonName={
          refundBooking ? occurrences.find((o) => o.bookingId === refundBooking.id)?.lessonName : undefined
        }
        defaultReason="Term lesson was cancelled. I would like to request a refund."
        onCancel={() => setRefundBooking(null)}
        onConfirm={handleRefundConfirm}
      />

      {absenceOccurrence && (
        <ReportAbsenceFromCalendar
          occurrence={absenceOccurrence}
          isOpen={!!absenceOccurrence}
          onClose={() => setAbsenceOccurrence(null)}
          onSuccess={handleAbsenceSuccess}
        />
      )}
    </>
  );
}
